import { useQuery } from '@apollo/client';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { GetSimilarJobsForUserDocument } from '@xing-com/crate-jobs-graphql';
import type { JobRecommendationFragment } from '@xing-com/crate-jobs-graphql';

import { useSimilarJobs } from './use-similar-jobs';

const LI_CONSUMER = 'loggedin.web.jobs.posting.similarjobsrecommendations';
const LO_CONSUMER = 'loggedout.web.jobs.posting.similarjobsrecommendations';

type SimilarJobsResponse = {
  loading: boolean;
  recommendations: (JobRecommendationFragment | null)[];
  similarJobs: (JobRecommendationFragment | null)[];
};

type Props = { limit: number; jobId?: string };

export const useSimilarJobsAndRecos = ({
  limit,
  jobId,
}: Props): SimilarJobsResponse => {
  const { isLoggedIn } = useLoginState();
  const { loading: similarJobsLoading, similarJobs } = useSimilarJobs({
    limit,
    jobId,
  });
  const { loading: jobRecosLoading, data } = useQuery(
    GetSimilarJobsForUserDocument,
    {
      variables: {
        consumer: isLoggedIn ? LI_CONSUMER : LO_CONSUMER,
        first: 9,
      },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    }
  );
  const recommendations = (
    data?.viewer?.similarJobsForUserRecommendations?.edges ?? []
  ).reduce<JobRecommendationFragment[]>(
    (prev, { node }) =>
      node.job?.__typename === 'VisibleJob' ? [...prev, node] : prev,
    []
  );
  const loading = similarJobsLoading || jobRecosLoading;

  return {
    loading,
    recommendations,
    similarJobs,
  };
};
